import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Menu, MessageCircle, Plus, PlusCircle, Check, X } from 'lucide-react';
import { Logo } from '@upsales/components';
import { useSelf } from 'App/components/hooks/appHooks';
import { useSoftDeployAccess, useFeatureAvailable } from 'App/components/hooks';
import useSelector from 'App/components/hooks/useSelector';
import { Feature } from 'Store/actions/FeatureHelperActions';
import ShadcnScope from 'App/shadcn/components/ShadcnScope';
import { TooltipProvider } from 'App/shadcn/ui/tooltip';
import WorkspaceChat from 'App/shadcn/pages/AIWorkspace/WorkspaceChat';
import workspaceStyles from 'App/shadcn/pages/AIWorkspace/workspaceStyles';
import praxisStyles from 'App/shadcn/pages/Assistant/praxisStyles';
import AssistantResource, { PageCompanyMatch, PageCompanyExtract } from 'Resources/Assistant';
import { agentColor } from 'App/shadcn/pages/AIWorkspace/components/AgentChip';
import { usePersonalAssistantIdentity } from 'App/shadcn/pages/AIWorkspace/usePersonalAssistantIdentity';
import type { Conversation } from 'App/shadcn/pages/Assistant/types';
import type { PageContext } from 'App/shadcn/pages/AIWorkspace/buildPageContextString';
import { notifyAIWorkspaceError } from 'App/shadcn/pages/AIWorkspace/notifyAIWorkspaceError';
import { isExtensionPanel, EXTENSION_ORIGINS } from './panelContext';
import AddToUpsalesTab from './AddToUpsalesTab';
import { matchablePageDomain } from './pageCompanyDomain';
import embedStyles from './embedStyles';
import T from 'Components/Helpers/translate';

// Only accept page-context messages from OUR extension specifically — not any extension.
const isExtensionOrigin = (origin: string): boolean => EXTENSION_ORIGINS.includes(origin);

// Chromeless AI chat for the Chrome side-panel iframe (WEB-7101). Renders full
// screen over the app shell (the MCPLogin pattern) and receives the current
// page's { url, title, text } from the extension via postMessage. Mirrors the
// AIWorkspace style/layout wrappers so WorkspaceChat is styled identically.
const EmbedAIChat: React.FC = () => {
	// Only the Upsales side-panel may use this route; a direct CRM visit bounces away.
	const inPanel = isExtensionPanel();
	useEffect(() => {
		if (!inPanel) window.location.hash = '#/';
	}, [inPanel]);

	const self = useSelf();
	const hasAiAssistant = useFeatureAvailable(Feature.AI_ASSISTANT);
	const hasAssistantSoftDeploy = useSoftDeployAccess('AI_ASSISTANT');
	const hasFeature = hasAiAssistant || hasAssistantSoftDeploy;
	// Features come from accountSelf (the AllIWant bootstrap), which is null until it loads.
	// The hooks above return false while it's null — indistinguishable from "no feature" — so we
	// track load state explicitly to avoid deciding access before the data is in.
	const accountSelf = useSelector(({ App }) => App.accountSelf);

	const [conversationId, setConversationId] = useState<number | null>(null);
	const [pageContext, setPageContext] = useState<PageContext | null>(null);
	// Bumped to remount WorkspaceChat for a fresh chat (mirrors AIWorkspace's chatKey).
	const [chatKey, setChatKey] = useState(0);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [conversations, setConversations] = useState<Conversation[]>([]);
	// Mirror the workspace access gate: feature flag AND (admin or per-user grant).
	const [workspaceGranted, setWorkspaceGranted] = useState<boolean | null>(null);
	// Auto-add tab (WEB-7280): cheap per-domain company match + on-demand extraction.
	const [activeTab, setActiveTab] = useState<'chat' | 'add'>('chat');
	const [match, setMatch] = useState<PageCompanyMatch | null>(null);
	const matchCache = useRef(new Map<string, PageCompanyMatch>());
	const extractCache = useRef(new Map<string, PageCompanyExtract>());
	// Assistant identity (avatar/name) — resolved the same way as the AI Workspace so the
	// message avatar matches (personal agent first, falling back to the profile).
	const { assistantName, avatarUrl, personalAgentId } = usePersonalAssistantIdentity(self?.id);

	useEffect(() => {
		const onMessage = (event: MessageEvent) => {
			if (!isExtensionOrigin(event.origin)) return;
			const data = event.data;
			if (!data || typeof data !== 'object' || data.type !== 'page-context') return;
			setPageContext({
				url: String(data.url ?? ''),
				title: String(data.title ?? ''),
				text: String(data.text ?? '')
			});
		};
		window.addEventListener('message', onMessage);
		// Ready ping to the panel. targetOrigin '*' is fine: it carries no data, and the
		// parent is the extension (chrome-extension://) whose origin we can't enumerate
		// here. The parent validates our origin before sending anything back.
		window.parent?.postMessage({ type: 'embed-ready' }, '*');
		return () => window.removeEventListener('message', onMessage);
	}, []);

	// Log out the panel when the user signs out in another tab (cross-tab signal from
	// loginHelper). Reloading re-runs the auth guard, which lands on the login screen.
	useEffect(() => {
		const onStorage = (e: StorageEvent) => {
			if (e.key === 'upsales:logout') window.location.reload();
		};
		window.addEventListener('storage', onStorage);
		return () => window.removeEventListener('storage', onStorage);
	}, []);

	useEffect(() => {
		if (self?.administrator) {
			setWorkspaceGranted(true);
			return;
		}
		AssistantResource.getMyPermissions()
			.then(p => setWorkspaceGranted(Boolean(p?.canAccessWorkspace)))
			.catch(err => {
				notifyAIWorkspaceError(err, 'getMyPermissions');
				setWorkspaceGranted(false);
			});
	}, [self?.administrator]);

	// Cheap company match per visited page: debounced, cached per domain, no LLM.
	// Only call the (workspace-guarded) endpoint once the user actually has access —
	// otherwise every navigation fires a background 403 + error toast.
	const canUseAutoAdd = hasFeature && (Boolean(self?.administrator) || workspaceGranted === true);

	useEffect(() => {
		if (!canUseAutoAdd || !pageContext?.url) return;
		const domain = matchablePageDomain(pageContext.url);
		if (!domain) {
			setMatch(null);
			return;
		}
		const cached = matchCache.current.get(domain);
		if (cached) {
			setMatch(cached);
			return;
		}
		const timer = setTimeout(() => {
			AssistantResource.pageCompanyMatch(pageContext.url)
				.then(result => {
					matchCache.current.set(domain, result);
					setMatch(result);
				})
				.catch(err => notifyAIWorkspaceError(err, 'pageCompanyMatch'));
		}, 1000);
		return () => clearTimeout(timer);
	}, [canUseAutoAdd, pageContext?.url]);

	const handleCompanyAdded = useCallback(
		(clientId: number, clientName: string) => {
			const domain = pageContext?.url ? matchablePageDomain(pageContext.url) : null;
			const updated: PageCompanyMatch = { status: 'existing', clientId, clientName };
			if (domain) matchCache.current.set(domain, updated);
			setMatch(updated);
		},
		[pageContext?.url]
	);

	const openDrawer = useCallback(() => {
		setDrawerOpen(true);
		AssistantResource.listConversations()
			.then(list =>
				setConversations(
					// Match the sidebar predicate (general/slack/legacy-undefined) + sort newest first.
					list
						.filter(c => !c.contextType || c.contextType === 'general' || c.contextType === 'slack')
						.sort((a, b) => new Date(b.modDate).getTime() - new Date(a.modDate).getTime())
				)
			)
			.catch(err => notifyAIWorkspaceError(err, 'listConversations'));
	}, []);

	const handleSelect = useCallback((conv: Conversation) => {
		setConversationId(conv.id);
		setDrawerOpen(false);
	}, []);

	const handleNewChat = useCallback(() => {
		setConversationId(null);
		setChatKey(k => k + 1);
		setDrawerOpen(false);
	}, []);

	// Not in the extension panel — render nothing while the redirect above runs.
	if (!inPanel) return null;

	const isAdmin = Boolean(self?.administrator);
	const hasAccess = hasFeature && (isAdmin || workspaceGranted === true);
	// While accountSelf hasn't loaded (the post-login reload races the AllIWant fetch) or the
	// per-user grant is still resolving, render nothing — otherwise we flash the no-access screen
	// at a user who actually has access.
	const accessResolved = accountSelf !== null && (!hasFeature || isAdmin || workspaceGranted !== null);
	if (!accessResolved) return null;

	return (
		<ShadcnScope style={{ position: 'fixed', inset: 0, zIndex: 100000 }}>
			<TooltipProvider delayDuration={200}>
				<style>{workspaceStyles}</style>
				<style>{praxisStyles}</style>
				<style>{embedStyles}</style>
				{hasAccess ? (
					<div className="ai-ws ai-ws-embed">
						<main className="ws-main">
							<div className="embed-topbar">
								<button
									type="button"
									className="embed-icon-btn"
									title={T('assistant.conversations')}
									aria-label={T('assistant.conversations')}
									onClick={openDrawer}
								>
									<Menu />
								</button>
							</div>
							{/* Hidden, not unmounted, when the add tab is active — unmounting
							    WorkspaceChat would kill an in-flight SSE stream. */}
							<div className={`ws-main-inner${activeTab === 'add' ? ' is-hidden' : ''}`}>
								{conversationId === null && (
									<div className="embed-empty">
										<div className="embed-empty-logo">
											<Logo color="green" />
										</div>
										<div className="embed-empty-tagline">{T('assistant.embedTagline')}</div>
									</div>
								)}
								<WorkspaceChat
									key={chatKey}
									assistantName={assistantName}
									avatarUrl={avatarUrl}
									avatarColor={personalAgentId ? agentColor(personalAgentId) : undefined}
									personalAgentId={personalAgentId}
									conversationId={conversationId}
									setConversationId={setConversationId}
									selfId={self?.id}
									contextType="general"
									pageContext={pageContext}
									hideTopbar
									hideWelcome
								/>
							</div>
							{activeTab === 'add' && pageContext ? (
								<AddToUpsalesTab
									pageContext={pageContext}
									extractCache={extractCache.current}
									onCompanyAdded={handleCompanyAdded}
								/>
							) : null}
							<nav className="embed-tabbar">
								<button
									type="button"
									className={`embed-tab${activeTab === 'chat' ? ' is-active' : ''}`}
									onClick={() => setActiveTab('chat')}
								>
									<MessageCircle />
									<span className="embed-tab-label">{T('assistant.embedTabChat')}</span>
								</button>
								<button
									type="button"
									className={`embed-tab${activeTab === 'add' ? ' is-active' : ''}`}
									onClick={() => setActiveTab('add')}
								>
									{match?.status === 'existing' ? <Check /> : <PlusCircle />}
									<span className="embed-tab-label">
										{match?.status === 'existing'
											? match.clientName
											: match?.status === 'new'
											? match.companyName
											: T('assistant.embedTabAdd')}
									</span>
									{match?.status === 'new' ? <span className="embed-tab-dot" /> : null}
								</button>
							</nav>
						</main>

						{/* Slide-in chat drawer (from the right) */}
						<div
							className={`embed-backdrop${drawerOpen ? ' is-open' : ''}`}
							onClick={() => setDrawerOpen(false)}
						/>
						<aside className={`embed-drawer${drawerOpen ? ' is-open' : ''}`}>
							<div className="embed-drawer-head">
								<span className="embed-drawer-title">{T('assistant.conversations')}</span>
								<button
									type="button"
									className="embed-icon-btn"
									title={T('default.close')}
									aria-label={T('default.close')}
									onClick={() => setDrawerOpen(false)}
								>
									<X />
								</button>
							</div>
							<button type="button" className="embed-new-chat" onClick={handleNewChat}>
								<Plus />
								{T('assistant.newChat')}
							</button>
							<div className="embed-conv-list">
								{conversations.map(conv => (
									<button
										key={conv.id}
										type="button"
										className={`embed-conv-item${conv.id === conversationId ? ' is-active' : ''}`}
										onClick={() => handleSelect(conv)}
									>
										{conv.title || T('assistant.newChat')}
									</button>
								))}
							</div>
						</aside>
					</div>
				) : (
					<div className="ai-ws ai-ws-no-access" role="alert">
						<div className="embed-no-access">
							<div className="embed-no-access-logo">
								<Logo color="green" />
							</div>
							<div className="embed-no-access-title">{T('assistant.embedNoAccessTitle')}</div>
							<div className="embed-no-access-body">
								{hasFeature
									? T('assistant.embedNoAccessNoUserGrant')
									: T('assistant.embedNoAccessNoFeature')}
							</div>
						</div>
					</div>
				)}
			</TooltipProvider>
		</ShadcnScope>
	);
};

export default EmbedAIChat;
